import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Drawer, Input, Radio, Tag } from 'antd';
import { T } from 'react-translator-component';

import {
  AgentInput,
  BlockSpacer,
  CommentBody,
  FieldGroup,
  FieldLabel,
  MutedNotice,
  RightSpacedButton,
  SpacedAlert,
  SpacedRadioGroup,
  TopSpacedButton,
  WarningNotice,
} from './styled';
import { CommentDate, TicketFields, TicketHistory } from './TicketContent';

import {
  applyTransition,
  assignTicket,
  getInternalTicket,
  getTransitions,
  sendInternalComment,
} from '../../../hooks/jira';
import { TicketDetail } from '../../../types/responses/Jira';

// Os tipos do antd 4.10 com @types/react 18 nao declaram `children` em Drawer/Radio.
const AntDrawer = Drawer as React.FC<React.ComponentProps<typeof Drawer> & { children?: React.ReactNode }>;
const AntRadioGroup = SpacedRadioGroup as React.FC<React.ComponentProps<typeof Radio.Group> & { children?: React.ReactNode }>;
const AntRadio = Radio as React.FC<React.ComponentProps<typeof Radio> & { children?: React.ReactNode }>;

// Destino da resposta: escolha explicita e obrigatoria, sem padrao (nada vai ao cliente por engano).
type Audience = 'customer' | 'internal';

const ACCOUNT_ID = /^[A-Za-z0-9:_-]{1,64}$/;

interface Props {
  ticketKey: string | null;
  onClose: () => void;
  onChanged: () => void;
}

const errorOf = (e: any, fallback: string) => (e?.response?.status === 403 ? 'You do not have access to this area' : fallback);

// Todo texto vindo do Jira (titulo, autores, comentarios) entra como texto React, nunca HTML.
const InternalTicketDrawer = ({ ticketKey, onClose, onChanged }: Props) => {
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [transitions, setTransitions] = useState<{ id: string; name: string }[]>([]);
  const [reply, setReply] = useState('');
  const [audience, setAudience] = useState<Audience | undefined>(undefined);
  const [transitionsFailed, setTransitionsFailed] = useState(false);
  const [agent, setAgent] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // `session` muda a cada troca/fechamento/desmontagem; `loadSeq` a cada busca iniciada.
  const session = useRef(0);
  const loadSeq = useRef(0);
  const busyRef = useRef(false);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const load = useCallback(async () => {
    if (!ticketKey) return;
    const current = session.current;
    loadSeq.current += 1;
    const call = loadSeq.current;
    // allSettled: falha nas transicoes nao esconde o ticket (so some com os botoes de status).
    const [d, t] = await Promise.allSettled([getInternalTicket(ticketKey), getTransitions(ticketKey)]);
    if (current !== session.current || call !== loadSeq.current) return;
    if (d.status === 'rejected') {
      setError(errorOf(d.reason, 'Could not load the ticket'));
      return;
    }
    setDetail(d.value);
    setTransitions(t.status === 'fulfilled' ? t.value?.values ?? [] : []);
    setTransitionsFailed(t.status === 'rejected');
    setError('');
  }, [ticketKey]);

  useEffect(() => {
    session.current += 1;
    busyRef.current = false;
    setBusy(false);
    setDetail(null);
    setTransitions([]);
    setReply('');
    setAudience(undefined);
    setTransitionsFailed(false);
    setAgent('');
    setError('');
    if (!ticketKey) return undefined;
    load();
    return () => {
      session.current += 1;
    };
  }, [ticketKey, load]);

  // Executa uma acao de escrita: ignora cliques enquanto ha uma pendente; resposta tardia
  // de outra sessao (ticket trocado/fechado) nao recarrega nada nem mexe no estado.
  const act = async (run: (key: string) => Promise<unknown>, failure: string, after?: () => void) => {
    if (!ticketKey || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    const current = session.current;
    try {
      await run(ticketKey);
      if (current !== session.current) return;
      after?.();
      await load();
      onChangedRef.current();
    } catch (e) {
      if (current === session.current) setError(errorOf(e, failure));
    } finally {
      if (current === session.current) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };

  const send = () => {
    const text = reply.trim();
    if (!text || !audience) return;
    // Sempre um boolean real: cliente => public=true; nota interna => public=false.
    const isPublic = audience === 'customer';
    act((k) => sendInternalComment(k, text, isPublic), 'Could not send the message', () => {
      setReply('');
      setAudience(undefined);
    });
  };

  const transition = (id: string) => act((k) => applyTransition(k, id), 'Could not change the status');

  const assign = () => {
    const id = agent.trim();
    if (!ACCOUNT_ID.test(id)) {
      setError('Invalid agent account id');
      return;
    }
    act((k) => assignTicket(k, id), 'Could not assign the ticket', () => setAgent(''));
  };

  return (
    <AntDrawer
      visible={!!ticketKey}
      onClose={onClose}
      width={560}
      title={detail?.summary.title || ticketKey || ''}
      destroyOnClose
    >
      {error && (
        <SpacedAlert
          type="error"
          message={T(error)}
          action={
            error === 'Could not load the ticket' ? (
              <Button size="small" onClick={load}>
                {T('Try again')}
              </Button>
            ) : undefined
          }
        />
      )}
      {detail && (
        <>
          <p>
            <strong>{detail.summary.key}</strong> · {detail.summary.statusLabel}
          </p>
          <TicketFields
            fields={detail.fields}
            fieldLabels={detail.fieldLabels}
            childTenantName={detail.summary.childTenantName}
          />
          {transitionsFailed && (
            <MutedNotice role="status">{T('Could not load the status changes')}</MutedNotice>
          )}
          <BlockSpacer>
            {transitions.map((t) => (
              <RightSpacedButton key={t.id} size="small" disabled={busy} onClick={() => transition(t.id)}>
                {t.name}
              </RightSpacedButton>
            ))}
          </BlockSpacer>
          <FieldGroup>
            <FieldLabel htmlFor="internal-agent">{T('Agent account id')}</FieldLabel>
            <AgentInput
              id="internal-agent"
              placeholder={T('Agent account id')}
              value={agent}
              maxLength={64}
              onChange={(e) => setAgent(e.target.value)}
            />
            <Button disabled={busy} onClick={assign}>
              {T('Assign')}
            </Button>
          </FieldGroup>
          <TicketHistory history={detail.history} />
          <h4>{T('Messages')}</h4>
          {detail.comments.map((c) => (
            <BlockSpacer key={c.id}>
              <strong>{c.author}</strong>
              <CommentDate iso={c.createdAt} /> {!c.public && <Tag color="gold">{T('Internal note')}</Tag>}
              <CommentBody>{c.body}</CommentBody>
            </BlockSpacer>
          ))}
          <FieldLabel htmlFor="internal-reply">{T('Write a reply')}</FieldLabel>
          <Input.TextArea
            id="internal-reply"
            rows={3}
            value={reply}
            placeholder={T('Write a reply')}
            onChange={(e) => setReply(e.target.value)}
          />
          <AntRadioGroup value={audience} onChange={(e) => setAudience(e.target.value as Audience)}>
            <AntRadio value="customer">{T('Reply to customer')}</AntRadio>
            <AntRadio value="internal">{T('Internal note (only visible to Move)')}</AntRadio>
          </AntRadioGroup>
          {audience === 'internal' && <WarningNotice role="status">{T('Only visible to Move')}</WarningNotice>}
          <br />
          <TopSpacedButton type="primary" loading={busy} disabled={!audience} onClick={send}>
            {T('Send')}
          </TopSpacedButton>
        </>
      )}
    </AntDrawer>
  );
};

export default InternalTicketDrawer;
